import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check if there's a saved theme in localStorage
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme') as Theme;
      if (savedTheme) {
        return savedTheme;
      }
      // Check system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      return newTheme;
    });
  };

  useEffect(() => {
    const root = window.document.documentElement;

    const applyClass = () => {
      // Remove previous theme class
      root.classList.remove('light', 'dark');

      // Add current theme class
      root.classList.add(theme);
    };

    // index.html already stamps the right class before first paint (to avoid
    // a flash of the wrong theme), so on mount this is a no-op: only an actual
    // toggle should pay for a view transition.
    const isActualToggle = !root.classList.contains(theme);

    // Cross-fade the whole page through the class swap instead of snapping,
    // unless the browser lacks View Transitions or the user asked for less motion.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isActualToggle && typeof document.startViewTransition === 'function' && !reducedMotion) {
      // The browser can legitimately skip/abort a transition (e.g. a rapid
      // second toggle); that rejection must not surface as an unhandled error.
      const transition = document.startViewTransition(() => applyClass());
      transition.ready.catch(() => {});
      transition.finished.catch(() => {});
    } else {
      applyClass();
    }

    // Save to localStorage
    localStorage.setItem('theme', theme);

    // Keep the browser chrome in sync with the manual toggle, not just the OS
    // preference the static <meta> tags track.
    const colour = theme === 'dark' ? '#0e0f14' : '#fcfcfd';
    document
      .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
      .forEach((m) => {
        m.removeAttribute('media');
        m.content = colour;
      });
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
